import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ReactComponent as ImpersonateIcon } from "assets/ImpersonateButtonIcon.svg";
import { setImpersonationDetails, setInnCode } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import SingleSelectFilter from "components/common/SingleSelectFilter";
import Typography from "components/common/Typography";
import AssignMultiplePlansDialog from "components/common/plans/AssignMultiplePlansDialog";
import { fetchAssigneeDetailsAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { fetchOwnerDetailsAsync } from "components/features/MyPlans/myplans.slice";
import { clearDashboardData, fetchDashboardReportAsync } from "components/features/dashboard/Dashboard.slice";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ABOVE_PROP_USER_TYPE, accessTypes, missingIncodeText } from "utils/CommonConstants";
import { getUserTypeTag } from "utils/CommonFunctions";
import { clearFilter, fetchMyPlansAsync, setLoader, setSearchText } from "../AboveProperty/AboveProperty.slice";
import FilterButtons from "../AboveProperty/FilterButtons";
import PropertyPlansList from "../AboveProperty/PropertyPlansList";
import { updateRecentlyViewedData } from "../Corp/CorpUser.Slice";
import SnapShot from "../IndividualProperty/SnapShot";

const UserView = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [multiplansAssign, setMultiplansAssign] = useState(false);
  const { isMobileScreen } = useContext(ThemeContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const innCode = searchParams.get("inncode") ?? "";
  const { ad } = useAppSelector((state) => state.myPlanDetails);
  const { filters } = useAppSelector((state) => state.aboveProperty);
  const isSmeUser = useAppSelector((state) => state.app.isSmeUser);
  const isAdmin = useAppSelector((state) => state.app.isAdmin);
  const isAbovePropertyUser = useAppSelector((state) => state.app.isAbovePropertyUser);
  const { status, source, functionalAreas, searchText } = filters.userView.plans;
  const inncodeOptions = ad?.length ? ad[0]?.innCodes : [];

  useEffect(() => {
    if (isAdmin) {
      dispatch(
        updateRecentlyViewedData({
          user: [
            {
              hltId: params?.hltId
            }
          ]
        })
      );
    }
    dispatch(fetchAssigneeDetailsAsync({ value: params?.hltId, size: 1 }));
  }, []);

  useEffect(() => {
    const apiCall = async () => {
      const payload = {
        includeOwners: true,
        searchedHltId: params?.hltId,
        innCode,
        searchText,
        status: status.join(","),
        functionalArea: functionalAreas.join(","),
        planSources: source.join(",")
      };
      const props = { payload, page };
      await dispatch(fetchMyPlansAsync(props)).then((res: any) => {
        if (res?.payload?.meta?.owners?.length) {
          dispatch(
            fetchOwnerDetailsAsync({ size: res?.payload?.meta?.owners?.length, value: res?.payload?.meta?.owners })
          );
        }
      });
      dispatch(setLoader(false));
    };

    apiCall();
  }, [status, source, functionalAreas, searchText, innCode, page]);

  useEffect(() => {
    setPage(0);
  }, [status, source, functionalAreas, innCode, searchText]);

  const fetchMore = () => {
    setPage((pageNo) => pageNo + 1);
  };
  const handleCloseDialog = () => {
    setMultiplansAssign(false);
  };

  const getDashboardReports = () => {
    dispatch(clearDashboardData());
    if (innCode && innCode !== missingIncodeText) {
      dispatch(
        fetchDashboardReportAsync({
          innCode,
          externalHltId: params?.hltId,
          list: "TOTAL_PLANS,PLANS_SNAPSHOT,CARDS_SNAPSHOT"
        })
      );
    }
  };

  const handleImpersonateUserClick = (member: any) => {
    const isAbovePropertyUser = String(member?.lobbyUserType).toLowerCase() === ABOVE_PROP_USER_TYPE;

    dispatch(
      setImpersonationDetails({
        isImpersonated: true,
        navigateToURL: `userview/${ad[0]["hltId"]}?inncode=${innCode}`,
        userDetails: { ...member, isAbovePropertyUser, isSmeUser }
      })
    );
    dispatch(setInnCode(innCode));
    navigate("/");
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ my: "24px" }}>
        <Typography variant="body2" color={tokens.Text.General.Secondary}>
          Inncode Selected
        </Typography>
        <Box flex={1} sx={{ maxWidth: "160px" }}>
          <SingleSelectFilter
            showTextField={false}
            label="Inncode"
            showSelectedOption
            options={inncodeOptions}
            selectedOption={innCode}
            onSubmit={(val: any) => {
              getDashboardReports();
              setSearchParams({ inncode: val });
              dispatch(clearFilter({ parentView: "userView", filterType: "plans" }));
              dispatch(
                setSearchText({
                  searchType: "plans",
                  filterName: "searchText",
                  parentView: "userView"
                })
              );
            }}
          />
        </Box>
      </Stack>
      <Stack
        direction={isMobileScreen ? "column" : "row"}
        alignItems={isMobileScreen ? "flex-start" : "center"}
        spacing={2}
      >
        <Stack flex="1" spacing={2}>
          <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline}>
            {ad?.[0]?.displayName}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography showBold variant="body2" color={tokens.Text.General.PrimaryHeadline}>
              User Type
            </Typography>
            <Typography variant="body2">: {getUserTypeTag(ad?.[0]?.lobbyUserType)}</Typography>
          </Stack>

          <Stack
            direction={isMobileScreen ? "column" : "row"}
            alignItems={isMobileScreen ? "flex-start" : "center"}
            spacing={1}
            sx={{ color: tokens.Text.General.PrimaryHeadline }}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{ pr: "6px", borderRight: isMobileScreen ? "none" : `1px solid ${tokens.Border.General.Secondary}` }}
            >
              <Typography showBold variant="body2">
                Role
              </Typography>
              <Typography variant="body2">: {ad?.[0]?.roles?.join(", ")}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
              <Typography showBold variant="body2">
                Last Login
              </Typography>
              <Typography variant="body2">
                : {ad?.[0]?.lastLoggedInDate && moment(ad[0]?.lastLoggedInDate).format("DD MMM, yyyy")}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          direction={isMobileScreen ? "row-reverse" : "row"}
          alignItems="center"
          justifyContent={isMobileScreen ? "center" : "flex-start"}
          spacing={3}
          sx={{ width: isMobileScreen ? "100%" : "auto" }}
        >
          {
            <>
              {isAbovePropertyUser && (
                <Box
                  sx={{ width: "50%" }}
                  component={Link}
                  to={`/userDashboard?member=${params?.hltId}&inncode=${innCode}`}
                >
                  <Button variant="secondary" label="View Dashboard" sx={{ width: "100%" }} />
                </Box>
              )}
              <Button
                label="Assign Plan"
                accessType={accessTypes.assign}
                sx={{ width: "40%" }}
                onClick={() => {
                  dispatch(clearFilter({ parentView: "dialogFilters", filterType: "planAssignment" }));
                  dispatch(
                    setSearchText({
                      inputText: "",
                      searchType: "planAssignment",
                      filterName: "searchText",
                      parentView: "dialogFilters"
                    })
                  );
                  setMultiplansAssign(true);
                }}
              />
            </>
          }

          {isAdmin && (
            <Button
              startIcon={<ImpersonateIcon />}
              onClick={() => handleImpersonateUserClick(ad?.[0])}
              variant="secondary"
              label="Impersonate user"
            />
          )}
        </Stack>
      </Stack>
      <SnapShot id={params?.hltId} isUser hltId={params?.hltId} innCode={innCode} />
      <Typography
        variant="h4"
        showBold
        color={tokens.Text.General.PrimaryHeadline}
        sx={{
          mt: "24px"
        }}
      >
        Plans
      </Typography>
      <FilterButtons
        isStatusVisible
        isSourceVisible
        isSearchBarVisible
        filterType="plans"
        parentView="userView"
        isFunctionalAreaVisible
        searchPredefinedValue={searchText}
        searchBarPlaceHolder="Search by plan"
      />
      <PropertyPlansList fetchMore={fetchMore} />
      {multiplansAssign && (
        <AssignMultiplePlansDialog
          setOpenAssignPlanDialog={setMultiplansAssign}
          handleCloseDialog={handleCloseDialog}
          selectedInncodeList={[{ innCode: innCode, hltId: params?.hltId, displayName: ad?.[0]?.displayName }]}
          individualPropertyInncode={innCode}
        />
      )}
    </>
  );
};
export default UserView;
